import React, { useState, useEffect } from 'react';
import { useUser, useClerk, useAuth } from '@clerk/clerk-react';
import SharedHeaderHomeowner from './SharedHeaderHomeowner';
// CB:BEGIN inline-help-shortcut-import
import InlineHelpShortcut from './help/InlineHelpShortcut';
// CB:END inline-help-shortcut-import
import { useProgressiveDataService, getTempSessionId, clearTempSessionId } from '../lib/progressiveDataService';
import { showDevNotification } from '../lib/devToast';
import { eventTracker } from './DataTracking';
import { useHeadlessAuth } from '../auth/headlessSignup';
import { getApiUrl } from '../lib/getApiUrl';
import { trackAnalyticsEvent } from '../lib/analytics';
import { getFrontendRuntime } from '../config/runtime';
import {
  setPostAuthRedirect,
  setRedirectLock,
  clearClerkRedirectParams,
} from '../auth/postAuth';
// CB:BEGIN estimate-form-validation
// Import Zod validation
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
// CB:END estimate-form-validation

// CB:BEGIN estimate-form-schema
// Zod schema for estimate form validation - defined after imports to avoid TDZ
const estimateFormSchema = z.object({
    startTimeline: z.string().min(1, 'Please select a start timeline'),
    workNeeded: z.string().min(1, 'Please select work needed'),
    reasonForWork: z.string().min(1, 'Please provide a reason for work'),
    roofType: z.string().min(1, 'Please select a roof type'),
    hasAddition: z.string().min(1, 'Please indicate if you have an addition'),
    insuranceCoverage: z.string().min(1, 'Please select insurance coverage'),
    needsFunding: z.string().min(1, 'Please indicate funding needs'),
    name: z.string().min(1, 'Name is required'),
    email: z.string().email('Please enter a valid email address'),
    phone: z.string().min(1, 'Phone number is required'),
    agreeToTerms: z.boolean().refine(val => val === true, 'You must agree to the terms'),
    agreeToTextMessages: z.boolean().refine(val => val === true, 'You must agree to receive text messages')
  });

type EstimateFormData = z.infer<typeof estimateFormSchema>;
// CB:END estimate-form-schema

interface EstimateFormProps {
  onNavigate: (screen: string) => void;
}

interface FormData {
  startTimeline: string;
  workNeeded: string;
  reasonForWork: string;
  roofType: string;
  hasAddition: string;
  insuranceCoverage: string;
  needsFunding: string;
  name: string;
  email: string;
  phone: string;
  agreeToTerms: boolean;
  agreeToTextMessages: boolean;
  // New fields for enhanced project data
  priority: string;
  tags: string[];
  notes: string;
  estimatedDuration: number | null;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
}

// CB:BEGIN canonical-address-resolver
/**
 * Canonical helper to resolve the best available address from localStorage.
 * enteredAddress is canonical; legacy keys are fallback only.
 * NEVER returns "TBD" or other placeholder values.
 * Returns empty string if no valid address is found.
 */
function resolveBestAddressFromStorage(): string {
  const BAD = new Set(['TBD', 'Address TBD', 'Address to be provided']);

  const parsePending = (): string => {
    const raw = localStorage.getItem('pendingUserAddress');
    if (!raw) return '';

    try {
      const parsed = JSON.parse(raw);
      const full =
        parsed?.fullAddress ||
        (parsed?.street && parsed?.city && parsed?.state && parsed?.zipCode
          ? `${parsed.street}, ${parsed.city}, ${parsed.state}, ${parsed.zipCode}`
          : '');
      return typeof full === 'string' ? full.trim() : '';
    } catch {
      // pendingUserAddress might already be a plain string
      return raw.trim();
    }
  };

  // Priority order: enteredAddress (canonical) > pendingUserAddress (legacy) > last_address (legacy)
  const candidates = [
    (localStorage.getItem('enteredAddress') || '').trim(),  // 1. Most recent, canonical
    parsePending(),                                         // 2. Legacy fallback
    (localStorage.getItem('last_address') || '').trim(),   // 3. Legacy fallback
  ];

  for (const c of candidates) {
    if (c && !BAD.has(c)) return c;
  }
  return '';
}
// CB:END canonical-address-resolver

const EstimateForm: React.FC<EstimateFormProps> = ({ onNavigate }) => {
  const { user, isSignedIn } = useUser();
  const { setActive } = useAuth();
  // CB:BEGIN remove-unused-variables
  // const { session } = useClerk(); // Removed - unused
  // const [existingUser, setExistingUser] = useState<any>(null); // Removed - unused
  // CB:END remove-unused-variables
  
  const { createOrUpdateTempHomeowner, updateHomeowner, getHomeowner, getTempHomeowner, updateProject, createProject, processSignup, ensureActiveProject } = useProgressiveDataService();
  const { silentSignIn } = useHeadlessAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    startTimeline: '',
    workNeeded: '',
    reasonForWork: '',
    roofType: '',
    hasAddition: '',
    insuranceCoverage: '',
    needsFunding: 'not-applicable',
    name: '',
    email: '',
    phone: '',
    agreeToTerms: true, // Checked by default
    agreeToTextMessages: true, // Checked by default
    // New fields initialization
    priority: 'MEDIUM',
    tags: [],
    notes: '',
    estimatedDuration: null
  });
  const [showPlanningModal, setShowPlanningModal] = useState(false);
  const [showPartialReplacementModal, setShowPartialReplacementModal] = useState(false);
  const [showOtherReasonModal, setShowOtherReasonModal] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [showErrors, setShowErrors] = useState(false);
  // CB:BEGIN preferred-contractor-state
  const [prefContractorState, setPrefContractorState] = useState('');
  const [prefContractorId, setPrefContractorId] = useState('');
  const [financingInterest, setFinancingInterest] = useState('');
  const [availableStates, setAvailableStates] = useState<string[]>([]);
  const [availableContractors, setAvailableContractors] = useState<{id:string,company:string}[]>([]);
  // CB:END preferred-contractor-state
  
  // CB:BEGIN headless-signup-state
  // Password fields for headless signup
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  
  // CB:BEGIN password-validation
  // Real-time password confirmation validation
  const [passwordErrors, setPasswordErrors] = useState<{[k:string]: string}>({});
  
  function validatePasswordField(name: string, value: string) {
    const newErrors: {[k:string]: string} = {};
    if (name === 'passwordConfirm' || name === 'password') {
      const pwd = name === 'password' ? value : password;
      const confirmValue = name === 'passwordConfirm' ? value : confirm;
      if (pwd && confirmValue && pwd !== confirmValue) {
        newErrors.passwordConfirm = 'Passwords do not match';
      }
    }
    return newErrors;
  }
  
  function handlePasswordChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    if (name === 'password') {
      setPassword(value);
    } else if (name === 'passwordConfirm') {
      setConfirm(value);
    }
    const fieldErrors = validatePasswordField(name, value);
    setPasswordErrors(prev => ({ ...prev, ...fieldErrors, ...(fieldErrors.passwordConfirm ? {} : { passwordConfirm: '' }) }));
  }
  // CB:END password-validation
  // CB:END headless-signup-state

  // CB:BEGIN form-validation-setup
  // React Hook Form setup with Zod validation
  const {
    // CB:BEGIN remove-unused-variables
    // register, // Removed - unused
    // handleSubmit: handleFormSubmit, // Removed - unused
    // formState: { errors, isSubmitting }, // Removed - unused
    // setValue, // Removed - unused
    // trigger // Removed - unused
    // CB:END remove-unused-variables
    
    formState: { errors },
    watch
  } = useForm<EstimateFormData>({
    resolver: zodResolver(estimateFormSchema),
    defaultValues: formData
  });

  // Watch form values for real-time validation
  // CB:BEGIN remove-unused-variables
  // const watchedValues = watch(); // Removed - unused
  // const getFieldError = (fieldName: keyof EstimateFormData): string | undefined => { // Removed - unused
  //   return errors[fieldName]?.message;
  // };
  // CB:END remove-unused-variables
  // CB:END form-validation-setup

  // CB:BEGIN load-available-states-on-mount
  useEffect(() => {
    fetch(getApiUrl('/api/contractors/available-states'))
      .then(r => r.ok ? r.json() : { states: [] })
      .then(data => setAvailableStates(data.states || []))
      .catch(() => {});
  }, []);
  // CB:END load-available-states-on-mount

  // CB:BEGIN load-available-contractors-on-state-change
  useEffect(() => {
    if (!prefContractorState) {
      setAvailableContractors([]);
      setPrefContractorId('');
      return;
    }
    fetch(getApiUrl(`/api/contractors/available-by-state?state=${encodeURIComponent(prefContractorState)}`))
      .then(r => r.ok ? r.json() : { contractors: [] })
      .then(data => setAvailableContractors(data.contractors || []))
      .catch(() => setAvailableContractors([]));
  }, [prefContractorState]);
  // CB:END load-available-contractors-on-state-change

  // Ensure there is an active project ID; if not, create one now
  useEffect(() => {
    const ensureProject = async () => {
      try {
        const existing = localStorage.getItem('active_project_id');
        if (existing && existing !== '0' && existing !== 'demo-project') {
          return;
        }

        // CB:BEGIN use-canonical-address-resolver-ensure-project
        const address = resolveBestAddressFromStorage();
        // CB:END use-canonical-address-resolver-ensure-project

        const projectType = localStorage.getItem('pendingProjectType');

        if (user) {
          const homeowner = await getHomeowner(user.id);
          await ensureActiveProject({
            homeownerId: homeowner.id,
            address,
            projectType,
            logLabel: '[EstimateForm]',
          });
        } else {
          const tempSessionId = getTempSessionId();
          const tempHomeowner = await getTempHomeowner(tempSessionId);
          await ensureActiveProject({
            tempHomeownerId: tempHomeowner.id,
            tempSessionId,
            address,
            projectType,
            logLabel: '[EstimateForm]',
          });
        }
      } catch (err) {
        console.error('❌ Failed to ensure active project', err);
      }
    };

    ensureProject();
  }, [user, getHomeowner, getTempHomeowner, ensureActiveProject]);

  // Prefill user info and checkboxes when user is signed in
  React.useEffect(() => {
    if (user) {
      const userData = {
        name: user.fullName || '',
        email: user.emailAddresses?.[0]?.emailAddress || '',
        phone: user.phoneNumbers?.[0]?.phoneNumber || ''
      };
      
      setFormData(prev => ({
        ...prev,
        name: prev.name || userData.name,
        email: prev.email || userData.email,
        phone: prev.phone || userData.phone,
        agreeToTerms: true,
        agreeToTextMessages: true
      }));
      
      console.log('✅ Pre-populated form with user data:', userData);
      console.log('👤 User signed in:', isSignedIn);
    }
  }, [user, isSignedIn]);

  // Handle sign-up completion and temp user merge
  React.useEffect(() => {
    const handleSignUpComplete = async () => {
      console.log('🔍 Checking sign-up completion:', { 
        isSignedIn, 
        hasUser: !!user, 
        currentStep, 
        userEmail: user?.emailAddresses?.[0]?.emailAddress 
      });
      
      if (isSignedIn && user) {
        console.log('🎯 User signed in, checking for temp user merge');
        
        // Check if migration has already been attempted for this user
        const migrationKey = `migration_attempted_${user?.id}`;
        if (localStorage.getItem(migrationKey)) {
          console.log('🔄 Migration already attempted for user:', user?.id);
          return;
        }
        
        const tempUuid = getTempSessionId();
        console.log('🔍 Temp UUID found:', tempUuid);
        
        if (tempUuid) {
          try {
            console.log('🔄 Merging temp user to permanent user:', { 
              tempUuid, 
              userUuid: user?.id, 
              email: user?.emailAddresses?.[0]?.emailAddress 
            });
            
            // Process signup and merge temp user to permanent user
            const result = await processSignup({
              user_id: user?.id,
              email: user?.emailAddresses?.[0]?.emailAddress || '',
              account_type: 'HOMEOWNER',
              temp_session_id: tempUuid
            });
            
            console.log('✅ Temp user merged successfully:', result);
            
            // Mark migration as attempted
            localStorage.setItem(migrationKey, 'true');
            
            // Dev-only placeholder: show welcome email toast immediately
            if (import.meta.env.DEV) {
              try {
                showDevNotification('Notification: Welcome email sent!');
              } catch (error) {
                console.warn('Failed to clear temp session:', error);
              }
            }
            
            // Clear temp session id after migration
            try { clearTempSessionId(); } catch (error) {
              console.warn('Failed to clear temp session ID:', error);
            }
            
            // Ensure a homeowner project exists after signup
            try {
              const activeProjectId = localStorage.getItem('active_project_id');
              if (!activeProjectId && user) {
                // create starter project for homeowner
                await updateProject({} as any, {} as any); // no-op safeguard
              }
            } catch (error) {
              console.warn('Failed to ensure homeowner project:', error);
            }
            
            // Navigate to measurements after successful merge
            const timer = setTimeout(() => {
              console.log('⏰ Navigating to roof-report-coming-soon after sign-up');
              onNavigate('roof-report-coming-soon');
            }, 2000);
            
            return () => clearTimeout(timer);
          } catch (error) {
            console.error('❌ Error merging temp user:', error);
          }
        } else {
          console.log('⚠️ No temp UUID found, skipping merge');
          
          // If no temp user but user is signed in, navigate to measurements
          if (currentStep === 8) {
            const timer = setTimeout(() => {
              console.log('⏰ Navigating to roof-report-coming-soon (no temp user)');
              onNavigate('roof-report-coming-soon');
            }, 2000);
            
            return () => clearTimeout(timer);
          }
        }
      } else {
        console.log('⏳ Waiting for sign-up completion:', { 
          isSignedIn, 
          hasUser: !!user, 
          currentStep 
        });
      }
    };

    handleSignUpComplete();
  }, [isSignedIn, user, currentStep, onNavigate]);

  // Save form data to temp user as user progresses (debounced to prevent infinite loops)
  React.useEffect(() => {
    const saveFormDataToTempUser = async () => {
      const tempUuid = getTempSessionId();
      
      if (tempUuid && !isSignedIn && currentStep > 1) {
        try {
          console.log('💾 Saving form data to temp user:', { tempUuid, currentStep });
          
          // CB:BEGIN use-canonical-address-resolver
          // Use canonical helper to resolve best address from localStorage
          const address = resolveBestAddressFromStorage();
          // CB:END use-canonical-address-resolver
          
          // CB:BEGIN save-questionnaire-to-meta
          // Save questionnaire data to meta field so it's transferred during registration
          // The backend will merge this into the meta field
          const questionnaireData = {
            questionnaire: {
              question1: "What is your preferred timeline for starting the project?",
              answer1: formData.startTimeline,
              question2: "What type of work do you need?",
              answer2: formData.workNeeded,
              question3: "What is the reason for this work?",
              answer3: formData.reasonForWork,
              question4: "What type of roof do you have?",
              answer4: formData.roofType,
              question5: "Do you have any additions or extensions?",
              answer5: formData.hasAddition,
              question6: "Do you have insurance coverage for this work?",
              answer6: formData.insuranceCoverage,
            }
          };
          // CB:END save-questionnaire-to-meta
          
          // Save current form data to temp homeowner (including questionnaire data in meta)
          // Only include address if we have a valid one (don't send undefined or 'TBD')
          const tempHomeownerData: any = {
            temp_session_id: tempUuid,
            name: formData.name,
            phone: formData.phone,
            sms_opt_in: formData.agreeToTextMessages,
            ...questionnaireData, // Spread questionnaire data so it goes into meta
          };
          
          // Only add address if we have a valid one (canonical helper already filters out TBD)
          if (address) {
            tempHomeownerData.address = address;
          }
          
          await createOrUpdateTempHomeowner(tempHomeownerData);
          
          console.log('✅ Form data saved to temp user successfully');
        } catch (error) {
          console.error('❌ Error saving form data to temp user:', error);
        }
      }
    };

    // Debounce the save operation to prevent infinite loops
    const timeoutId = setTimeout(saveFormDataToTempUser, 500);
    return () => clearTimeout(timeoutId);
  }, [currentStep, formData.name, formData.phone, formData.agreeToTextMessages, isSignedIn]);

  const totalSteps = 8; // 7 questions + preferred contractor + contact
  const progressPercentage = (currentStep / totalSteps) * 100;

  // Validation functions
  const validateEmail = (email: string): string | null => {
    if (!email.trim()) {
      return 'Email is required';
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    
    // Check for common email issues
    if (email.includes('..') || email.includes('@@')) {
      return 'Please enter a valid email address';
    }
    
    const [localPart, domain] = email.split('@');
    if (localPart.length === 0 || domain.length === 0) {
      return 'Please enter a valid email address';
    }
    
    if (domain.split('.').length < 2) {
      return 'Please enter a valid email address';
    }
    
    // Check for trailing dots
    if (email.endsWith('.') || domain.endsWith('.')) {
      return 'Please enter a valid email address';
    }
    
    return null;
  };

  const validatePhone = (phone: string): string | null => {
    if (!phone.trim()) {
      return 'Phone number is required';
    }
    
    // Remove all non-digit characters for validation
    const digitsOnly = phone.replace(/\D/g, '');
    
    if (digitsOnly.length < 10) {
      return 'Phone number must have at least 10 digits';
    }
    
    if (digitsOnly.length > 15) {
      return 'Phone number is too long';
    }
    
    // Check for common phone number patterns
    const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
    if (!phoneRegex.test(digitsOnly)) {
      return 'Please enter a valid phone number';
    }
    
    return null;
  };

  const validateName = (name: string): string | null => {
    if (!name.trim()) {
      return 'Name is required';
    }
    
    if (name.trim().length < 2) {
      return 'Name must be at least 2 characters long';
    }
    
    if (name.trim().length > 50) {
      return 'Name is too long (maximum 50 characters)';
    }
    
    // Check for valid characters (letters, spaces, hyphens, apostrophes)
    const nameRegex = /^[a-zA-Z\s\-'.]+$/;
    if (!nameRegex.test(name.trim())) {
      return 'Name can only contain letters, spaces, hyphens, and apostrophes';
    }
    
    return null;
  };

  const validateField = (field: keyof FormData, value: string): void => {
    let error: string | null = null;
    
    switch (field) {
      case 'email':
        error = validateEmail(value);
        break;
      case 'phone':
        error = validatePhone(value);
        break;
      case 'name':
        error = validateName(value);
        break;
    }
    
    setFormErrors(prev => ({
      ...prev,
      [field]: error || undefined
    }));
  };

  const handleInputChange = (field: keyof FormData, value: string | boolean, autoAdvance?: boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'insuranceCoverage' && value !== 'no' ? { needsFunding: 'not-applicable' } : {})
    }));
    
    // Validate on change for text fields
    if (typeof value === 'string' && ['email', 'phone', 'name'].includes(field)) {
      // Clear error when user starts typing
      if (formErrors[field as keyof FormErrors]) {
        setFormErrors(prev => ({ ...prev, [field]: undefined }));
      }
      
      // Validate after a short delay to avoid too frequent validation
      setTimeout(() => {
        validateField(field, value);
      }, 300);
    }

    if (autoAdvance) {
      setTimeout(() => handleNext(), 0);
    }
  };

  const persistStepToProject = async (step: number, answers: FormData) => {
    const projectId = localStorage.getItem('active_project_id');
    const tempSessionId = getTempSessionId();
    
    if (!projectId && !tempSessionId) return;
    
    try {
      // Store questionnaire data in a structured way
      const questionnaireData = {
        startTimeline: answers.startTimeline,
        workNeeded: answers.workNeeded,
        reasonForWork: answers.reasonForWork,
        roofType: answers.roofType,
        hasAddition: answers.hasAddition,
        insuranceCoverage: answers.insuranceCoverage
      };
      
      // If user is signed in, update project directly with questionnaire
      if (isSignedIn && projectId) {
        await updateProject(projectId, {
          questionnaire: questionnaireData,
          priority: answers.priority,
          tags: answers.tags,
          notes: answers.notes,
          estimatedDuration: answers.estimatedDuration
        });
      } 
      // If user is not signed in, update temp homeowner
      else if (!isSignedIn && tempSessionId) {
        // CB:BEGIN use-canonical-address-resolver-progress-save
        // Use canonical helper to resolve best address from localStorage
        const address = resolveBestAddressFromStorage();
        // CB:END use-canonical-address-resolver-progress-save
        
        const tempHomeownerData: any = {
          temp_session_id: tempSessionId,
          name: answers.name || '',
          phone: answers.phone || '',
          sms_opt_in: answers.agreeToTextMessages || false,
          questionnaireData: questionnaireData,
          priority: answers.priority,
          tags: answers.tags,
          notes: answers.notes,
          estimatedDuration: answers.estimatedDuration
        };
        
        // Only add address if we have a valid one (canonical helper already filters out TBD)
        if (address) {
          tempHomeownerData.address = address;
        }
        
        await createOrUpdateTempHomeowner(tempHomeownerData);
      }
    } catch (err) {
      console.error('❌ Failed to persist step answers', err);
    }
  };

  const handleNext = async () => {
    if (currentStep < totalSteps) {
      await persistStepToProject(currentStep, formData);
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // COREBIDS FIX: create project and request report for manual-bids flow
  const createProjectAndRequestReport = async () => {
    try {
      const token = await user?.getToken();
      if (!token) throw new Error('No authentication token');
      
      // Get address from localStorage or form data
      // CB:BEGIN use-canonical-address-resolver-project-creation
      // Use canonical helper, fallback to name's Property only if no address found
      const resolved = resolveBestAddressFromStorage();
      const address = resolved || `${formData.name}'s Property`;
      // CB:END use-canonical-address-resolver-project-creation
      
      // CB:BEGIN create-project-with-duplicate-guard
      const projectBody: Record<string, any> = {
        address: address,
        sqft: null,
        roofDetails: formData.roofType,
        priority: formData.priority,
        tags: formData.tags,
        notes: formData.notes || `Timeline: ${formData.startTimeline}, Work: ${formData.workNeeded}, Reason: ${formData.reasonForWork}`,
        estimatedDuration: formData.estimatedDuration,
        questionnaire: {
          question1: "What is your preferred timeline for starting the project?",
          answer1: formData.startTimeline,
          question2: "What type of work do you need?",
          answer2: formData.workNeeded,
          question3: "What is the reason for this work?",
          answer3: formData.reasonForWork,
          question4: "What type of roof do you have?",
          answer4: formData.roofType,
          question5: "Do you have any additions or extensions?",
          answer5: formData.hasAddition,
          question6: "Do you have insurance coverage for this work?",
          answer6: formData.insuranceCoverage,
        },
      };
      // CB:BEGIN preferred-contractor-in-project-body
      if (prefContractorId) projectBody.preferredContractorId = prefContractorId;
      if (prefContractorState) projectBody.preferredContractorState = prefContractorState;
      if (financingInterest) projectBody.financingInterest = financingInterest;
      // CB:END preferred-contractor-in-project-body
      const projectHeaders = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
      // Create project with questionnaire data
      let projectResponse = await fetch(getApiUrl('/api/projects'), {
        method: 'POST',
        headers: projectHeaders,
        body: JSON.stringify(projectBody),
      });

      // Handle duplicate warning: show confirmation and retry with override
      if (projectResponse.status === 409) {
        const dupData = await projectResponse.json().catch(() => ({}));
        if (dupData.error === 'DUPLICATE_PROJECT_WARNING') {
          trackAnalyticsEvent('duplicate_project_warning_shown', { path: '/estimate-form' });
          const proceed = window.confirm(
            `${dupData.message}\n\nExisting project: ${dupData.existingProjectAddress || 'same address'}\n\nClick OK to create a new project anyway, or Cancel to return.`
          );
          if (!proceed) return;
          projectResponse = await fetch(getApiUrl('/api/projects'), {
            method: 'POST',
            headers: projectHeaders,
            body: JSON.stringify({ ...projectBody, allowDuplicate: true }),
          });
        }
      }
      // CB:END create-project-with-duplicate-guard
      if (!projectResponse.ok) {
        throw new Error('Failed to create project');
      }

      const projectPayload = await projectResponse.json();
      const projectId = projectPayload?.projectId || projectPayload?.id || projectPayload?.project?.id;

      if (!projectId) {
        throw new Error('Project response missing projectId');
      }

      trackAnalyticsEvent('project_created', {
        projectId,
        path: '/estimate-form',
      });
      
      // Request report
      const reportResponse = await fetch(getApiUrl('/api/report-requests'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ projectId })
      });
      
      if (!reportResponse.ok) {
        throw new Error('Failed to request report');
      }

      trackAnalyticsEvent('report_purchased', {
        projectId,
        path: '/estimate-form',
      });
      
      // Navigate to next steps (not directly to dashboard)
      console.log('🎯 EstimateForm: Navigating to roof-report-coming-soon');
      onNavigate('roof-report-coming-soon');
      
      // CB:BEGIN clear-navigation-block-after-success
      // Clear the navigation block after successful navigation
      setTimeout(() => {
        (window as any).__CB_BLOCK_AUTO_NAV = false;
        console.log('🔓 EstimateForm: Cleared navigation block after successful navigation');
      }, 1000);
      // CB:END clear-navigation-block-after-success
      
    } catch (error) {
      console.error('❌ Error creating project and requesting report:', error);
      // Fallback to original navigation
      onNavigate('roof-report-coming-soon');
      
      // Clear the navigation block after fallback navigation too
      setTimeout(() => {
        (window as any).__CB_BLOCK_AUTO_NAV = false;
        console.log('🔓 EstimateForm: Cleared navigation block after fallback navigation');
      }, 1000);
    }
  };

  const handleSubmit = async () => {
    console.log('🔍 handleSubmit called, isSignedIn:', isSignedIn);
    
    if (busy) return;
    setPwError(null);
    
    if (!isSignedIn) {
      console.log('📝 User not signed in, starting headless signup flow');
      
      // Clerk-compatible password validation
      const okLen = password.length >= 8;
      const hasLetter = /[A-Za-z]/.test(password);
      const hasNumber = /\d/.test(password);
      const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
      
      if (!okLen) {
        setPwError('Password must be at least 8 characters long.');
        return;
      }
      if (!hasLetter) {
        setPwError('Password must contain at least one letter.');
        return;
      }
      if (!hasNumber) {
        setPwError('Password must contain at least one number.');
        return;
      }
      if (!hasSpecialChar) {
        setPwError('Password must contain at least one special character (!@#$%^&*).');
        return;
      }
      if (password !== confirm) {
        setPwError('Passwords do not match.');
        return;
      }
      
      // Validate all required fields before proceeding
      const nameError = validateName(formData.name);
      const emailError = validateEmail(formData.email);
      const phoneError = validatePhone(formData.phone);
      
      console.log('🔍 Validation results:', { 
        name: formData.name, 
        nameError, 
        email: formData.email, 
        emailError, 
        phone: formData.phone, 
        phoneError 
      });
      
      // Set form errors to trigger red outline highlighting
      const errors = {
        name: nameError || undefined,
        email: emailError || undefined,
        phone: phoneError || undefined
      };
      
      console.log('🔍 Setting form errors:', errors);
      setFormErrors(errors);
      
      // Show errors only after button is clicked
      setShowErrors(true);
      console.log('🔍 Setting showErrors to true, showErrors will be:', true);
      
      // If there are validation errors, don't proceed
      if (nameError || emailError || phoneError) {
        console.log('❌ Form validation failed:', { nameError, emailError, phoneError });
        return;
      }
      
      setBusy(true);
      try {
        // Ensure temp session id is available for migration
        const temp_session_id = getTempSessionId();
        
        // Save all form data to temp homeowner before registration
        console.log('💾 Saving all form data to temp homeowner before registration');
        // CB:BEGIN use-canonical-address-resolver-registration
        // Use canonical helper to resolve best address from localStorage
        const address = resolveBestAddressFromStorage();
        console.log('📍 Address extracted for registration:', { 
          address: address || 'NOT_FOUND (will use temp homeowner address if available)', 
          hasPendingUserAddress: !!localStorage.getItem('pendingUserAddress'),
          hasEnteredAddress: !!localStorage.getItem('enteredAddress'),
          hasLastAddress: !!localStorage.getItem('last_address')
        });
        // CB:END use-canonical-address-resolver-registration
        // CB:BEGIN save-questionnaire-to-meta
        // Save questionnaire data to meta field so it's transferred during registration
        const questionnaireData = {
          questionnaire: {
            question1: "What is your preferred timeline for starting the project?",
            answer1: formData.startTimeline,
            question2: "What type of work do you need?",
            answer2: formData.workNeeded,
            question3: "What is the reason for this work?",
            answer3: formData.reasonForWork,
            question4: "What type of roof do you have?",
            answer4: formData.roofType,
            question5: "Do you have any additions or extensions?",
            answer5: formData.hasAddition,
            question6: "Do you have insurance coverage for this work?",
            answer6: formData.insuranceCoverage,
          }
        };
        // CB:END save-questionnaire-to-meta
        
        // Save all form data to temp homeowner before registration (including questionnaire)
        const tempHomeownerData: any = {
          temp_session_id,
          name: formData.name,
          phone: formData.phone,
          sms_opt_in: formData.agreeToTextMessages,
          priority: formData.priority,
          tags: formData.tags,
          notes: formData.notes,
          estimatedDuration: formData.estimatedDuration,
          ...questionnaireData, // Spread questionnaire data so it goes into meta
        };
        
        // Only add address if we have a valid one (canonical helper already filters out TBD)
        if (address) {
          tempHomeownerData.address = address;
        }
        
        await createOrUpdateTempHomeowner(tempHomeownerData);
        
        // 1) create/ensure Clerk user silently (server-side)
          // CB:BEGIN auth-endpoint-baseurl
        // Use runtime config to get the correct API base URL
        const runtime = getFrontendRuntime();
        const apiBase = runtime.apiBaseUrl || '';
        // If apiBase is empty, use relative path (will be handled by vercel.json rewrites)
        const registerUrl = apiBase 
          ? `${apiBase.replace(/\/$/, '')}/api/auth/register`
          : '/api/auth/register';
        console.log('🔗 Registration URL:', registerUrl, 'API Base:', apiBase || '(relative - using vercel.json rewrite)');
        
        // CB:BEGIN dont-send-tbd-address
        // Don't send 'TBD' address in registration - let backend use temp homeowner address
        // CB:BEGIN homeowner-register-role
        const registerBody: any = {
          email: formData.email,
          password,
          firstName: formData.name?.split(' ')[0] || formData.name || 'User',
          lastName: formData.name?.split(' ').slice(1).join(' ') || 'Name',
          phone: formData.phone,
          temp_session_id,
          textOptIn: formData.agreeToTextMessages,
          role: 'homeowner', // Estimate flow = homeowner; backend sets Clerk usertype so provision creates Homeowner
        };
        // CB:END homeowner-register-role
        
        // Only include address if we have a valid one (canonical helper already filters out TBD)
        if (address) {
          registerBody.address = address;
        }
        // CB:END dont-send-tbd-address
        
        const registerResponse = await fetch(registerUrl, {
        // CB:END auth-endpoint-baseurl
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(registerBody),
        });

        if (!registerResponse.ok) {
          let body: any = null;
          try { body = await registerResponse.json(); } catch {}
          const code = body?.error || 'register_failed';
          // backend now returns `message` with a user-friendly string; fall back to error field
          const msg = body?.message || body?.error || 'Registration failed. Please try again.';
          throw Object.assign(new Error(msg), { code });
        }

        const registerData = await registerResponse.json();

        // Cache the usertype immediately to prevent role gate issues
        if (registerData.usertype) {
          localStorage.setItem('cb.usertype', registerData.usertype);
          console.log('✅ Cached usertype from registration:', registerData.usertype);
        }

        // 2) Handle authentication and redirect
        // Set redirect lock and target before attempting sign-in
        setPostAuthRedirect('/next-steps');
        setRedirectLock();
        clearClerkRedirectParams();
        
        // CB:BEGIN prevent-auto-redirect-during-signup
        // Set a flag to prevent any auto-redirects during the signup process
        (window as any).__CB_BLOCK_AUTO_NAV = true;
        console.log('🔒 EstimateForm: Blocking auto-navigation during signup');
        // CB:END prevent-auto-redirect-during-signup
        
        // CB:BEGIN improved-auth-flow
        // Use the token from registration response to sign in immediately
        if (registerData.token && registerData.userId) {
          console.log('🔐 Using registration token for immediate sign-in');
          try {
            // Import the silentTicketSignIn function
            const { silentTicketSignIn } = await import('../auth/headlessSignup');
            
            // Use the token directly from registration response
            const clerk = (window as any).Clerk;
            if (clerk) {
              await clerk.load();
              console.log('🔐 Attempting to sign in with token:', registerData.token?.substring(0, 20) + '...');
              const si = await clerk.client.signIn.create({ 
                strategy: 'ticket', 
                ticket: registerData.token 
              });
              console.log('✅ Sign-in result:', si?.status, si?.createdSessionId);
              if (si?.status === 'complete' && si?.createdSessionId) {
                await clerk.setActive({ session: si.createdSessionId });
                console.log('✅ User signed in successfully with registration token');
                
                // Set flags to indicate successful sign-in
                localStorage.setItem('just_signed_up', 'true');
                localStorage.setItem('just_signed_up_timestamp', Date.now().toString());
                
                // Safety: set post-auth redirect in case of race conditions
                setPostAuthRedirect('/next-steps');
                
                // Navigate to next steps
                onNavigate('roof-report-coming-soon');
                return;
              }
              // CB:BEGIN client-trust-homeowner-redirect – Client Trust: ticket required second factor; send user to /sign-in to complete
              if (si?.status === 'needs_second_factor') {
                setPostAuthRedirect('/next-steps');
                setRedirectLock();
                window.location.href = '/sign-in';
                return;
              }
              // CB:END client-trust-homeowner-redirect
            }
          } catch (signInError) {
            console.error('❌ Token-based sign-in failed:', signInError);
          }
        }
        
        // Fallback: Try the old silent sign-in method
        console.log('🔐 Attempting fallback silent sign-in for:', formData.email);
        try {
          // Add a small delay to ensure user is fully created in Clerk
          await new Promise(resolve => setTimeout(resolve, 1500));
          const ok = await silentSignIn(formData.email, password);
          if (ok) {
            console.log('✅ Silent sign-in successful');
          } else {
            console.log('ℹ️ Silent sign-in not completed; continuing with fallback.');
          }
        } catch (signInError) {
          console.error('❌ Silent sign-in failed:', signInError);
          console.log('🔄 User was created in Clerk. Setting up fallback authentication...');
          
          // CB:BEGIN fallback-auth
          // Store credentials for potential retry or manual sign-in
          localStorage.setItem('pendingAuth', JSON.stringify({
            email: formData.email,
            password: password,
            userId: registerData.userId,
            timestamp: Date.now()
          }));
          console.log('💾 Stored credentials for fallback authentication');
          // CB:END fallback-auth
          // Do not auto-open modal here; keep existing fallback
        }
        
        // Sign in the user first, then navigate
        console.log('🔐 Attempting to sign in after registration...');
        
        // Store credentials for manual sign-in
        localStorage.setItem('pendingSignIn', JSON.stringify({
          email: formData.email,
          password: password,
          timestamp: Date.now()
        }));
        
        // Navigate to next steps - user will be signed in via Clerk redirect
        console.log('🎯 Navigating to next steps after signup');
        setPostAuthRedirect('/next-steps');
        onNavigate('roof-report-coming-soon');
        return; // Exit early to prevent further processing
        // CB:END improved-auth-flow
      } catch (err: any) {
        console.error('Contact step submit failed', err);
        // FIX: Map common codes to friendly copy
        if (err.code === "form_password_pwned") {
          setPwError("This password was found in a data breach. Please choose a different, unique password.");
        } else if (err.code === "form_identifier_exists") {
          setPwError("An account with this email already exists. Try signing in.");
        } else if (err.code === "email_exists") {
          setPwError("An account with this email already exists. Try signing in.");
        } else if (err.code === "missing_fields") {
          setPwError("Please provide both email and password.");
        } else if (err.code === "env_misconfig") {
          setPwError("Service temporarily unavailable. Please try again shortly.");
        } else if (err.code === "invalid_email") {
          setPwError("Please enter a valid email address.");
        } else if (err.code === "password_too_short") {
          setPwError("Password must be at least 8 characters long.");
        } else if (err.code === "password_no_letter") {
          setPwError("Password must contain at least one letter.");
        } else if (err.code === "password_no_number") {
          setPwError("Password must contain at least one number.");
        } else if (err.code === "password_no_special") {
          setPwError("Password must contain at least one special character.");
        } else {
          setPwError(err.message || "Registration failed. Please try again.");
        }
      } finally {
        setBusy(false);
      }
      return;
    }
    if (!formData.agreeToTerms) return;
    
    // For signed-in users, just navigate to measurements
    if (isSignedIn) {
      console.log('✅ Signed-in user continuing to measurements');
      try {
        await persistStepToProject(currentStep, formData);
        await updateHomeowner({
          user_id: user?.id,
          name: formData.name || user?.fullName || undefined,
          phone: formData.phone || user?.phoneNumbers?.[0]?.phoneNumber || undefined,
          sms_opt_in: !!formData.agreeToTextMessages,
        });
      } catch (e) { console.error('❌ homeowner save failed', e); }
      // COREBIDS FIX: create project and request report for manual-bids flow
      await createProjectAndRequestReport();
      return;
    }
    
    // Validate all fields before submission (for non-signed-in users)
    const nameError = validateName(formData.name);
    const emailError = validateEmail(formData.email);
    const phoneError = validatePhone(formData.phone);
    
    if (nameError || emailError || phoneError) {
      setFormErrors({
        name: nameError || undefined,
        email: emailError || undefined,
        phone: phoneError || undefined
      });
      setShowErrors(true);
      console.log('❌ Form validation failed:', { nameError, emailError, phoneError });
      return;
    }
    
    // Clear stored address from localStorage
    localStorage.removeItem('pendingUserAddress');
    
    // COREBIDS FIX: create project and request report for manual-bids flow
    setShowErrors(false);
    await createProjectAndRequestReport();
  };



  const canProceed = () => {
    switch (currentStep) {
      case 1: return formData.startTimeline !== '';
      case 2: return formData.workNeeded !== '';
      case 3: return formData.reasonForWork !== '';
      case 4: return formData.roofType !== '';
      case 5: return formData.hasAddition !== '';
      case 6: return formData.insuranceCoverage !== '';
      case 7: return true; // Preferred contractor is optional
      case 8: {
        // For signed-in users, just check if terms are agreed to
        if (isSignedIn) {
          return formData.agreeToTerms;
        }

        // For non-signed-in users, check all required fields
        const hasRequiredFields = formData.name !== '' && formData.email !== '' && formData.phone !== '' && formData.agreeToTerms;

        // Check if there are any validation errors
        const hasNoErrors = !formErrors.name && !formErrors.email && !formErrors.phone;

        return hasRequiredFields && hasNoErrors;
      }
      default: return false;
    }
  };

  const renderQuestion = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="question-container">
            <h3 style={{ fontSize: 24, fontWeight: 600, marginBottom: 24 }}>When do you want to start?</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button
                className={`option-button ${formData.startTimeline === 'emergency' ? 'selected' : ''}`}
                onClick={() => handleInputChange('startTimeline', 'emergency', true)}
              >
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Emergency</div>
                <div style={{ fontSize: 14, color: '#6b7280' }}>Immediate Attention</div>
              </button>
              <button
                className={`option-button ${formData.startTimeline === 'soon' ? 'selected' : ''}`}
                onClick={() => handleInputChange('startTimeline', 'soon', true)}
              >
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Soon</div>
                <div style={{ fontSize: 14, color: '#6b7280' }}>Within 1-2 Weeks</div>
              </button>
              <button
                className={`option-button ${formData.startTimeline === 'planned' ? 'selected' : ''}`}
                onClick={() => handleInputChange('startTimeline', 'planned', true)}
              >
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Planned</div>
                <div style={{ fontSize: 14, color: '#6b7280' }}>Within 30 days</div>
              </button>
              <button
                className={`option-button ${formData.startTimeline === 'not-sure' ? 'selected' : ''}`}
                onClick={() => setShowPlanningModal(true)}
              >
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Still Planning & Budgeting</div>
                <div style={{ fontSize: 14, color: '#6b7280' }}>Exploring options</div>
              </button>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="question-container">
            <h3 style={{ fontSize: 24, fontWeight: 600, marginBottom: 24 }}>Work needed</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button
                className={`option-button ${formData.workNeeded === 'full-replacement' ? 'selected' : ''}`}
                onClick={() => handleInputChange('workNeeded', 'full-replacement', true)}
              >
                Full Roof Replacement
              </button>
              <button
                className={`option-button ${formData.workNeeded === 'partial-replacement' ? 'selected' : ''}`}
                onClick={() => setShowPartialReplacementModal(true)}
              >
                Partial Roof Replacement
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="question-container">
            <h3 style={{ fontSize: 24, fontWeight: 600, marginBottom: 24 }}>Reason for Roof Work</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button
                className={`option-button ${formData.reasonForWork === 'lifespan-over' ? 'selected' : ''}`}
                onClick={() => handleInputChange('reasonForWork', 'lifespan-over', true)}
              >
                End of Lifespan
              </button>
              <button
                className={`option-button ${formData.reasonForWork === 'leaking' ? 'selected' : ''}`}
                onClick={() => handleInputChange('reasonForWork', 'leaking', true)}
              >
                Leaking or Water Damage
              </button>
              <button
                className={`option-button ${formData.reasonForWork === 'damaged' ? 'selected' : ''}`}
                onClick={() => handleInputChange('reasonForWork', 'damaged', true)}
              >
                Cracked or Damaged
              </button>
              <button
                className={`option-button ${formData.reasonForWork === 'other' ? 'selected' : ''}`}
                onClick={() => setShowOtherReasonModal(true)}
              >
                Other
              </button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="question-container">
            <h3 style={{ fontSize: 24, fontWeight: 600, marginBottom: 24 }}>New Roof Type</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button
                className={`option-button ${formData.roofType === 'asphalt' ? 'selected' : ''}`}
                onClick={() => handleInputChange('roofType', 'asphalt', true)}
              >
                Asphalt Shingles
              </button>
              <button
                className={`option-button ${formData.roofType === 'metal' ? 'selected' : ''}`}
                onClick={() => handleInputChange('roofType', 'metal', true)}
              >
                Metal
              </button>
              <button
                className={`option-button ${formData.roofType === 'tile' ? 'selected' : ''}`}
                onClick={() => handleInputChange('roofType', 'tile', true)}
              >
                Tile
              </button>
              <button
                className={`option-button ${formData.roofType === 'flat' ? 'selected' : ''}`}
                onClick={() => handleInputChange('roofType', 'flat', true)}
              >
                Flat
              </button>
              <button
                className={`option-button ${formData.roofType === 'unsure' ? 'selected' : ''}`}
                onClick={() => handleInputChange('roofType', 'unsure', true)}
              >
                Unsure
              </button>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="question-container">
            <h3 style={{ fontSize: 24, fontWeight: 600, marginBottom: 24 }}>Has your house had an addition put on that affected your overall Square Footage in the last 4 years?</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button
                className={`option-button ${formData.hasAddition === 'yes' ? 'selected' : ''}`}
                onClick={() => handleInputChange('hasAddition', 'yes', true)}
              >
                Yes
              </button>
              <button
                className={`option-button ${formData.hasAddition === 'no' ? 'selected' : ''}`}
                onClick={() => handleInputChange('hasAddition', 'no', true)}
              >
                No
              </button>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="question-container">
            <h3 style={{ fontSize: 24, fontWeight: 600, marginBottom: 24 }}>Insurance Coverage used for Replacement?</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button
                className={`option-button ${formData.insuranceCoverage === 'fully-approved' ? 'selected' : ''}`}
                onClick={() => handleInputChange('insuranceCoverage', 'fully-approved', true)}
              >
                Yes - claim approved
              </button>
              <button
                className={`option-button ${formData.insuranceCoverage === 'pending' ? 'selected' : ''}`}
                onClick={() => handleInputChange('insuranceCoverage', 'pending', true)}
              >
                Maybe - claim pending
              </button>
              <button
                className={`option-button ${formData.insuranceCoverage === 'no' ? 'selected' : ''}`}
                onClick={() => handleInputChange('insuranceCoverage', 'no')}
              >
                No
              </button>
            </div>
            {formData.insuranceCoverage === 'no' && (
              <div style={{ marginTop: 16, padding: 16, background: '#f0f9ff', borderRadius: 8, border: '1px solid #bae6fd' }}>
                <h4 style={{ marginBottom: 8, color: '#1e40af' }}>Want to check out our payment plan options?</h4>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button
                    className={`btn ${formData.needsFunding === 'yes' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => handleInputChange('needsFunding', 'yes', true)}
                  >
                    Yes
                  </button>
                  <button
                    className={`btn ${formData.needsFunding === 'no' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => handleInputChange('needsFunding', 'no', true)}
                  >
                    No
                  </button>
                  <button
                    className={`btn ${formData.needsFunding === 'not-sure' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => handleInputChange('needsFunding', 'not-sure', true)}
                  >
                    Not Sure
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      case 7:
        return (
          <div className="question-container">
            <h3 style={{ fontSize: 24, fontWeight: 600, marginBottom: 8 }}>Preferred Contractor (Optional)</h3>
            <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 20 }}>If you already have a contractor in mind, select them below. Otherwise, skip to the next step.</p>

            {availableStates.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14 }}>Select a State</label>
                <select
                  value={prefContractorState}
                  onChange={e => setPrefContractorState(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                >
                  <option value="">-- No preference --</option>
                  {availableStates.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}

            {prefContractorState && availableContractors.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14 }}>Select a Contractor</label>
                <select
                  value={prefContractorId}
                  onChange={e => setPrefContractorId(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                >
                  <option value="">-- No preference --</option>
                  {availableContractors.map(c => <option key={c.id} value={c.id}>{c.company}</option>)}
                </select>
              </div>
            )}

            {prefContractorId && (
              <div style={{ padding: 12, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, marginBottom: 16, fontSize: 13, color: '#1e40af' }}>
                You will still receive two additional contractor bids through CoreBids.
              </div>
            )}

            <div style={{ marginTop: 20 }}>
              <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>Would you like to explore payment plan options?</p>
              <div style={{ display: 'flex', gap: 10 }}>
                {(['YES', 'NOT_SURE', 'NO'] as const).map(opt => (
                  <button
                    key={opt}
                    className={`btn ${financingInterest === opt ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setFinancingInterest(opt)}
                    style={{ flex: 1 }}
                  >
                    {opt === 'YES' ? 'Yes' : opt === 'NOT_SURE' ? 'Not Sure' : 'No'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 8:
        return (
          <div className="question-container" style={{ minHeight: '300px', justifyContent: 'flex-start', padding: '16px 0' }}>
            <h3 style={{ fontSize: 20, fontWeight: 600, marginBottom: 16 }}>Contact Information</h3>

            {isSignedIn && (
              <div style={{ 
                background: '#f0f9ff', 
                border: '1px solid #bae6fd', 
                borderRadius: '6px', 
                padding: '8px', 
                marginBottom: '12px',
                color: '#1e40af',
                fontSize: '12px'
              }}>
                ✅ Your contact information has been pre-filled from your account
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '14px' }}>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`form-control ${formErrors.name ? 'error' : ''}`}
                  placeholder="Full Name"
                  disabled={!!user}
                  style={{ padding: '8px 12px', fontSize: '14px' }}
                />
                {formErrors.name && showErrors && (
                  <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '2px' }}>
                    ⚠️ {formErrors.name}
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '14px' }}>Email Address *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`form-control ${formErrors.email ? 'error' : ''}`}
                  placeholder="your.email@example.com"
                  disabled={!!user}
                  style={{ padding: '8px 12px', fontSize: '14px' }}
                />
                {formErrors.email && showErrors && (
                  <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '2px' }}>
                    ⚠️ {formErrors.email}
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '14px' }}>Phone Number *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={`form-control ${formErrors.phone ? 'error' : ''}`}
                  placeholder="(555) 555-5555"
                  disabled={!!user}
                  style={{ padding: '8px 12px', fontSize: '14px' }}
                />
                {formErrors.phone && showErrors && (
                  <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '2px' }}>
                    ⚠️ {formErrors.phone}
                  </div>
                )}
              </div>
              
              {/* CB:BEGIN headless-signup-password-fields */}
              {!isSignedIn && (
                <div style={{ marginTop: '8px' }}>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '14px' }}>Password *</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type={showPw ? 'text' : 'password'}
                    required
                    name="password"
                    value={password}
                    onChange={handlePasswordChange}
                    className="form-control"
                    placeholder="Create a password"
                    autoComplete="new-password"
                    style={{ padding: '8px 12px', fontSize: '14px', flex: 1 }}
                  />
                    <button 
                      type="button" 
                      onClick={() => setShowPw((s) => !s)} 
                      className="btn btn-secondary"
                      style={{ padding: '8px 12px', fontSize: '12px', minWidth: '60px' }}
                    >
                      {showPw ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <label style={{ display: 'block', marginBottom: 4, marginTop: '8px', fontWeight: 600, fontSize: '14px' }}>Confirm Password *</label>
                  <input
                    type={showPw ? 'text' : 'password'}
                    required
                    name="passwordConfirm"
                    value={confirm}
                    onChange={handlePasswordChange}
                    className="form-control"
                    placeholder="Re-enter your password"
                    autoComplete="new-password"
                    style={{ padding: '8px 12px', fontSize: '14px' }}
                  />
                  {passwordErrors.passwordConfirm && <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '2px' }}>{passwordErrors.passwordConfirm}</p>}
                  {pwError && <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '2px' }}>{pwError}</p>}
                </div>
              )}
              {/* CB:END headless-signup-password-fields */}
              <div style={{ marginTop: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: '13px', fontWeight: '400' }}>
                  <input
                    id="q8-terms"
                    type="checkbox"
                    checked={formData.agreeToTerms || !!user}
                    onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                    required
                    disabled={!!user}
                    style={{ 
                      width: '12px', 
                      height: '12px', 
                      accentColor: '#2563eb',
                      cursor: 'pointer',
                      margin: '0'
                    }}
                  />
                  <span style={{ color: '#1f2937' }}>I Agree to the <a href="/terms" style={{ color: '#2563eb', textDecoration: 'underline', cursor: 'pointer' }} target="_blank" rel="noopener noreferrer">Terms & Conditions</a> *</span>
                </label>
              </div>
              <div style={{ marginTop: 4 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '13px', fontWeight: '400' }}>
                  <input
                    id="q8-text"
                    type="checkbox"
                    checked={formData.agreeToTextMessages || !!user}
                    onChange={(e) => handleInputChange('agreeToTextMessages', e.target.checked)}
                    disabled={!!user}
                    style={{ 
                      width: '12px', 
                      height: '12px', 
                      accentColor: '#2563eb',
                      cursor: 'pointer',
                      margin: '0'
                    }}
                  />
                  <span style={{ color: '#1f2937' }}>I Agree to receive text messages</span>
                </label>
              </div>
          <div className="consent-text" style={{ fontSize: 11, color: '#6b7280', marginTop: 6, lineHeight: '1.3' }}>
              By entering your number & checking I agree to recieve text messages, you agree to receive automated texts from CoreBids, Inc. at 877-570-7485 for customer care, project updates, and support. Msg frequency varies. Msg & data rates may apply. Text STOP to cancel, HELP for help. See <a href="/terms" style={{ color: '#2563eb', textDecoration: 'underline', cursor: 'pointer' }} target="_blank" rel="noopener noreferrer">Terms & Conditions</a> and <a href="#" onClick={(e) => { e.preventDefault(); onNavigate('privacy-policy'); }} style={{ color: '#2563eb', textDecoration: 'underline', cursor: 'pointer' }} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.              </div>
            </div>
          </div>
        );


      default:
        return null;
    }
  };

  return (
    <div className="container light-mode-override">
      <style>{`
        input[type="checkbox"]:checked {
          background-color: white !important;
          border-color: #d1d5db !important;
        }
        /* CB:BEGIN estimate-q8-checkbox */
        #q8-terms, #q8-text {
          width: 16px !important;
          height: 16px !important;
          min-width: 16px !important;
          min-height: 16px !important;
          max-width: 16px !important;
          max-height: 16px !important;
          transform: scale(0.75) !important;
          transform-origin: left center !important;
          appearance: none; -webkit-appearance: none; -moz-appearance: none;
          border: 1px solid #d1d5db; border-radius: 2px;
          background-color: white; cursor: pointer; margin: 0;
        }
        #q8-terms:checked, #q8-text:checked {
          background-color: white !important;
          border-color: #d1d5db !important;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23000000' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M6 10l3 3 6-6'/%3e%3c/svg%3e") !important;
          background-size: 100% 100% !important;
          background-position: center !important;
          background-repeat: no-repeat !important;
          box-shadow: none !important;
        }
        /* CB:END estimate-q8-checkbox */
        /* CB:BEGIN mobile-wizard-tighten */
        @media (max-width: 480px) {
          .container.light-mode-override {
            padding: 8px !important;
          }
          .container.light-mode-override .content {
            padding: 0 !important;
          }
          .container.light-mode-override h2 {
            font-size: 21px !important;
            margin-bottom: 10px !important;
          }
          .question-container h3 {
            font-size: 20px !important;
            margin-bottom: 14px !important;
            line-height: 1.2 !important;
          }
          .wizard-progress {
            margin-bottom: 14px !important;
          }
          .wizard-progress span {
            font-size: 12.5px !important;
          }
          .wizard-nav {
            position: sticky;
            bottom: 0;
            background: #ffffff;
            padding: 8px 0 4px 0;
            margin-top: 14px !important;
            gap: 10px !important;
            z-index: 5;
          }
          .wizard-nav .btn {
            width: 48%;
            min-height: 44px;
            padding: 10px 12px !important;
            font-size: 16px !important;
          }
          .wizard-card {
            margin-bottom: 10px !important;
          }
          .wizard-option {
            padding: 12px 12px !important;
            font-size: 16px !important;
            line-height: 1.25 !important;
          }
          .wizard-option small {
            font-size: 13.5px !important;
            line-height: 1.3 !important;
          }
          .wizard-option + .wizard-option {
            margin-top: 6px !important;
          }
          .consent-text {
            font-size: 11px !important;
            line-height: 1.35 !important;
            max-height: 120px;
            overflow: auto;
            margin-top: 6px !important;
          }
        }
        /* CB:END mobile-wizard-tighten */
      `}</style>
      <SharedHeaderHomeowner onNavigate={onNavigate} />

      <div className="content">
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          {/* CB:BEGIN inline-help-shortcut-submit-details */}
          <InlineHelpShortcut
            onNavigate={onNavigate}
            sectionId="submitting-project-details"
            label="Need help submitting details?"
          />
          {/* CB:END inline-help-shortcut-submit-details */}
          <h2 style={{ textAlign: 'center', marginBottom: 16 }}>Get Your Roofing Estimates</h2>
          
          {/* Progress Bar */}
          <div className="wizard-progress" style={{ marginBottom: 32 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 14, color: '#6b7280' }}>Question {currentStep} of {totalSteps}</span>
              <span style={{ fontSize: 14, color: '#6b7280' }}>{Math.round(progressPercentage)}%</span>
            </div>
            <div style={{ 
              width: '100%', 
              height: 8, 
              background: '#e5e7eb', 
              borderRadius: 4,
              overflow: 'hidden'
            }}>
              <div style={{ 
                width: `${progressPercentage}%`, 
                height: '100%', 
                background: '#2563eb',
                transition: 'width 0.3s ease'
              }}></div>
            </div>
          </div>

          {/* Question */}
          {renderQuestion()}

          {/* Navigation Buttons */}
          <div className="wizard-nav" style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32 }}>
            <button 
              className="btn btn-secondary" 
              onClick={handleBack}
              disabled={currentStep === 1}
            >
              Back
            </button>
            {currentStep < totalSteps && currentStep === 7 && (
              <button
                className="btn btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
            {currentStep === totalSteps && (
              <button
                className="btn btn-primary"
                onClick={() => {
                  eventTracker.track('bid_request_submitted');
                  handleSubmit();
                }}
                disabled={busy}
              >
                {busy ? 'Working…' : (!isSignedIn ? 'Get 3 Bids!' : 'Get 3 Bids!')}
              </button>
            )}
          </div>

          {/* CB:BEGIN inline-validation-helper */}
          {!isSignedIn && showErrors && (
            <div style={{ marginTop: 12, fontSize: 12, color: '#b91c1c' }}>
              {formErrors.name || formErrors.email || formErrors.phone ? (
                <>
                  Please fix the highlighted fields
                  {formErrors.name ? ' (Name)' : ''}
                  {formErrors.email ? ' (Email)' : ''}
                  {formErrors.phone ? ' (Phone)' : ''}
                  .
                </>
              ) : null}
              {!formData.agreeToTerms && (
                <div>Please agree to the Terms to continue.</div>
              )}
            </div>
          )}
          {/* CB:END inline-validation-helper */}

          {/* CB:BEGIN feedback-button */}
          {/* Feedback button removed */}
          {/* CB:END feedback-button */}
        </div>
      </div>

      {/* CB:BEGIN headless-signup-removed-modal */}
      {/* Clerk modal components removed - using headless signup instead */}
      {/* CB:END headless-signup-removed-modal */}
      


      {showPlanningModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '40px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
            textAlign: 'center'
          }}>
            <h2 style={{ marginBottom: '24px', fontSize: '24px', fontWeight: '600' }}>
              Still planning?
            </h2>
            
            <p style={{ marginBottom: '16px', fontSize: '18px', color: '#374151' }}>
              Come back and see us soon!
            </p>
            
            <div style={{ 
              background: '#f9fafb', 
              padding: '20px', 
              borderRadius: '8px', 
              marginBottom: '24px',
              border: '2px solid #e5e7eb'
            }}>
              <p style={{ fontSize: '16px', color: '#374151', lineHeight: '1.5', margin: 0 }}>
                We encourage you to have a solidified start date before completing this process. Upon completion of this form your home will have qualified contractor bids waiting for you to accept within 48 hours.
              </p>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button 
                className="btn btn-primary"
                onClick={() => onNavigate('landing')}
                style={{ padding: '12px 24px' }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowPlanningModal(false);
                  handleInputChange('startTimeline', 'not-sure');
                  handleNext();
                }}
                style={{ padding: '12px 24px' }}
              >
                Continue with Roofing Estimate
              </button>
            </div>
          </div>
        </div>
      )}

      {showOtherReasonModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '40px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
            textAlign: 'center'
          }}>
            <h2 style={{ marginBottom: '24px', fontSize: '24px', fontWeight: '600' }}>
              Other Reason for Roof Work
            </h2>
            
            <p style={{ marginBottom: '16px', fontSize: '18px', color: '#374151' }}>
              Please specify the reason for your roof work.
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ fontSize: '14px', color: '#6b7280', textAlign: 'left', marginBottom: '8px' }}>
                Specify:
              </label>
              <input
                type="text"
                value={formData.reasonForWork === 'other' ? '' : formData.reasonForWork}
                onChange={(e) => handleInputChange('reasonForWork', e.target.value)}
                className="form-control"
                placeholder="Enter reason..."
                style={{ 
                  padding: '12px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '24px' }}>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  setShowOtherReasonModal(false);
                  handleNext();
                }}
                disabled={!formData.reasonForWork || formData.reasonForWork === 'other'}
                style={{ padding: '12px 24px' }}
              >
                Save & Continue
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowOtherReasonModal(false)}
                style={{ padding: '12px 24px' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showPartialReplacementModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '40px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
            textAlign: 'center'
          }}>
            <h2 style={{ marginBottom: '24px', fontSize: '24px', fontWeight: '600' }}>
              Partial Roof Replacement
            </h2>
            
            <p style={{ marginBottom: '16px', fontSize: '18px', color: '#374151' }}>
              Come back and see us soon!
            </p>
            
            <div style={{ 
              background: '#f9fafb', 
              padding: '20px', 
              borderRadius: '8px', 
              marginBottom: '24px',
              border: '2px solid #e5e7eb'
            }}>
              <p style={{ fontSize: '16px', color: '#374151', lineHeight: '1.5', margin: 0 }}>
                We are only capable of estimating and replacing full roof replacements at this time. Upon completion of this form your home will have qualified contractor bids waiting for you to accept within 48 hours.
              </p>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button 
                className="btn btn-primary"
                onClick={() => onNavigate('landing')}
                style={{ padding: '12px 24px' }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowPartialReplacementModal(false);
                  handleInputChange('workNeeded', 'full-replacement');
                  handleNext();
                }}
                style={{ padding: '12px 24px' }}
              >
                Continue with Full Roof Replacement
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
         .option-button {
           width: 100%;
           padding: 16px;
           border: 2px solid #e5e7eb;
           border-radius: 8px;
           background: white;
           text-align: left;
           cursor: pointer;
           transition: all 0.2s;
           font-size: 16px;
         }
         
         .option-button:hover {
           border-color: #2563eb;
           background: #f8fafc;
         }
         
         .option-button.selected {
           border-color: #2563eb;
           background: #eff6ff;
           color: #1e40af;
         }
         
         .question-container {
           min-height: 400px;
           display: flex;
           flex-direction: column;
           justify-content: center;
         }
       `}</style>
     </div>
   );
 };

 export default EstimateForm;